import { Injectable, Inject, PLATFORM_ID } from "@angular/core";
import { isPlatformBrowser } from "@angular/common";

declare let gtag: any;
declare let posthog: any;

interface WebVitalsMetric {
  name: string;
  value: number;
  rating: "good" | "needs-improvement" | "poor";
  delta: number;
  id: string;
  navigationType?: string;
}

@Injectable({
  providedIn: "root",
})
export class WebVitalsService {
  private isBrowser: boolean;
  private metricsCollected = new Set<string>();

  constructor(@Inject(PLATFORM_ID) private platformId: object) {
    this.isBrowser = isPlatformBrowser(this.platformId);
  }

  initWebVitals(): void {
    if (!this.isBrowser) return;

    this.observeCLS();
    this.observeLCP();
    this.observeFID();
    this.observeINP();
    this.observeFCP();
    this.observeTTFB();
  }

  private observeCLS(): void {
    if (!this.isBrowser || !("PerformanceObserver" in window)) return;

    let clsValue = 0;
    let clsEntries: PerformanceEntry[] = [];

    const observer = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        if (!(entry as any).hadRecentInput) {
          clsValue += (entry as any).value;
          clsEntries.push(entry);
        }
      }
    });

    observer.observe({ type: "layout-shift", buffered: true });

    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "hidden") {
        this.reportMetric({
          name: "CLS",
          value: clsValue,
          rating: this.getRating("CLS", clsValue),
          delta: clsValue,
          id: this.generateId(),
        });
        observer.disconnect();
      }
    });
  }

  private observeLCP(): void {
    if (!this.isBrowser || !("PerformanceObserver" in window)) return;

    let lcpValue = 0;

    const observer = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      const lastEntry = entries[entries.length - 1];
      lcpValue = lastEntry.startTime;
    });

    observer.observe({ type: "largest-contentful-paint", buffered: true });

    const reportLCP = () => {
      this.reportMetric({
        name: "LCP",
        value: lcpValue,
        rating: this.getRating("LCP", lcpValue),
        delta: lcpValue,
        id: this.generateId(),
      });
      observer.disconnect();
    };

    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "hidden") {
        reportLCP();
      }
    });

    window.addEventListener("pagehide", reportLCP);
  }

  private observeFID(): void {
    if (!this.isBrowser || !("PerformanceObserver" in window)) return;

    const observer = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        const fidValue = (entry as any).processingStart - entry.startTime;
        this.reportMetric({
          name: "FID",
          value: fidValue,
          rating: this.getRating("FID", fidValue),
          delta: fidValue,
          id: this.generateId(),
        });
      }
      observer.disconnect();
    });

    observer.observe({ type: "first-input", buffered: true });
  }

  private observeINP(): void {
    if (!this.isBrowser || !("PerformanceObserver" in window)) return;

    let maxDuration = 0;

    const observer = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        const duration = (entry as any).duration || 0;
        if (duration > maxDuration) {
          maxDuration = duration;
        }
      }
    });

    try {
      observer.observe({ 
        type: "event", 
        buffered: true, 
        durationThreshold: 40 
      } as PerformanceObserverInit);
    } catch (e) {
      console.warn("[Web Vitals] INP observation not supported");
      return;
    }

    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "hidden") {
        this.reportMetric({
          name: "INP",
          value: maxDuration,
          rating: this.getRating("INP", maxDuration),
          delta: maxDuration,
          id: this.generateId(),
        });
        observer.disconnect();
      }
    });
  }

  private observeFCP(): void {
    if (!this.isBrowser || !("PerformanceObserver" in window)) return;

    const observer = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        if (entry.name === "first-contentful-paint") {
          const fcpValue = entry.startTime;
          this.reportMetric({
            name: "FCP",
            value: fcpValue,
            rating: this.getRating("FCP", fcpValue),
            delta: fcpValue,
            id: this.generateId(),
          });
          observer.disconnect();
        }
      }
    });

    observer.observe({ type: "paint", buffered: true });
  }

  private observeTTFB(): void {
    if (!this.isBrowser) return;

    const navigationEntry = performance.getEntriesByType(
      "navigation"
    )[0] as PerformanceNavigationTiming;

    if (navigationEntry) {
      const ttfbValue = navigationEntry.responseStart;
      this.reportMetric({
        name: "TTFB",
        value: ttfbValue,
        rating: this.getRating("TTFB", ttfbValue),
        delta: ttfbValue,
        id: this.generateId(),
        navigationType: navigationEntry.type,
      });
    }
  }

  private getRating(
    metric: string,
    value: number
  ): "good" | "needs-improvement" | "poor" {
    const thresholds: { [key: string]: [number, number] } = {
      CLS: [0.1, 0.25],
      LCP: [2500, 4000],
      FID: [100, 300],
      INP: [200, 500],
      FCP: [1800, 3000],
      TTFB: [800, 1800],
    };

    const [good, poor] = thresholds[metric] || [0, 0];

    if (value <= good) return "good";
    if (value <= poor) return "needs-improvement";
    return "poor";
  }

  private reportMetric(metric: WebVitalsMetric): void {
    if (this.metricsCollected.has(metric.name)) return;
    this.metricsCollected.add(metric.name);

    console.log(`[Web Vitals] ${metric.name}:`, {
      value: Math.round(metric.value),
      rating: metric.rating,
    });

    if (typeof gtag !== "undefined") {
      gtag("event", metric.name, {
        event_category: "Web Vitals",
        event_label: metric.id,
        value: Math.round(metric.value),
        metric_rating: metric.rating,
        metric_delta: Math.round(metric.delta),
        non_interaction: true,
      });
    }

    if (typeof posthog !== "undefined" && typeof posthog.capture === "function") {
      posthog.capture("web_vitals", {
        metric_name: metric.name,
        metric_value: Math.round(metric.value),
        metric_rating: metric.rating,
        metric_delta: Math.round(metric.delta),
        navigation_type: metric.navigationType,
      });
    }
  }

  private generateId(): string {
    return `v3-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
  }
}
